import { Button, Form, Input, Select, InputNumber, DatePicker } from 'antd';
import { useCallback, useState, useEffect } from 'react';
import { requestWithAuth } from '../../services/api';
import { industryList, staticFunctionFlags } from '../../utils/constants';
import { selectOptionFormatterFunctionFlag } from '../../utils/general';

const SignedRefcodes = () => {
  const [form] = Form.useForm();
  const [signedRefcode, setSignedRefcode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [accountRep, setAccountRep] = useState([]);
  const [theme, setTheme] = useState([]);
  const [pricePoints, setPricePoints] = useState({});
  const [functionFlags] = useState([...staticFunctionFlags]);
  const [refcodeExpiryText, setRefcodeExpiryText] = useState('');

  const selectedReferrerCode = Form.useWatch('referrer_code', form);
  const isUnlimited = ['premium_unlimited', 'premium_unlimited_nwm'].includes(
    selectedReferrerCode,
  );

  const UNLIMITED_PRICE_POINTS = [
    'monthly_3_trial_componentized',
    'annual_3_trial_componentized',
  ];

  const fetchAccountRep = () => {
    const params = {
      key: 'account_rep',
    };
    requestWithAuth('admin-field-lookup', 'GET', params, null)
      .then((response) => {
        if (response && response.result && response.result.length) {
          setAccountRep(response.result);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const fetchTheme = () => {
    const params = {
      key: 'theme',
    };
    requestWithAuth('admin-field-lookup', 'GET', params, null)
      .then((response) => {
        if (response && response.result && response.result.length) {
          setTheme(response.result);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const fetchPricePoint = () => {
    const params = {
      key: 'maxio_constants',
    };
    requestWithAuth('admin-field-lookup', 'GET', params, null)
      .then((response) => {
        if (
          response &&
          response.result &&
          response.result.price_point_descriptions
        ) {
          setPricePoints(response.result.price_point_descriptions);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const selectOptionFormatterAccountRep = ({ email }) => {
    return { value: email, label: email };
  };
  const selectOptionFormatterIndustry = ({ value }) => {
    return { value: value, label: value };
  };
  const selectOptionFormatterTheme = ({ theme__id, theme__name }) => {
    return { value: theme__id, label: theme__name };
  };
  const selectOptionFormatterPricePoints = ([ppKey, ppValue]: [
    string,
    string,
  ]) => {
    return { value: ppKey, label: ppValue };
  };

  useEffect(() => {
    fetchAccountRep();
    fetchTheme();
    fetchPricePoint();
  }, []);

  const setRequest = useCallback((body, parentKey, key, value) => {
    if (!value) {
      return;
    }
    if (!(parentKey in body)) {
      body[parentKey] = {};
    }
    body[parentKey][key] = value;
  }, []);

  const getRefcodeExpiryFromUrl = (url) => {
    try {
      const parsedUrl = new URL(url);
      const ttlParam = parsedUrl.searchParams.get('ttl');

      if (!ttlParam) {
        return 'This link doesn’t expire';
      }

      const ttlHours = parseInt(ttlParam, 10);
      if (isNaN(ttlHours)) {
        return 'Invalid TTL';
      }

      const expiryDateTime = new Date();
      expiryDateTime.setHours(expiryDateTime.getHours() + ttlHours);

      return expiryDateTime.toLocaleString();
    } catch {
      return 'Unable to determine expiry';
    }
  };

  const handleFinish = useCallback(
    (e) => {
      const body = {};

      ['ttl', 'campaign', 'price_point'].map((key) => {
        if (e[key]) {
          body[key] = e[key];
        }
      });

      const UNLIMITED_PLAN_MAP: Record<string, string> = {
        premium_unlimited: 'premium_unlimited',
        premium_unlimited_nwm: 'premium_unlimited_nwm',
      };
      if (e.referrer_code && e.referrer_code in UNLIMITED_PLAN_MAP) {
        setRequest(
          body,
          'subscription',
          'plan',
          UNLIMITED_PLAN_MAP[e.referrer_code],
        );
      } else if (e.referrer_code) {
        body['referrer_code'] = e.referrer_code;
      }

      setRequest(body, 'subscription', 'owner', e.owner);
      setRequest(body, 'subscription', 'max_seats', e.max_seats);
      setRequest(body, 'subscription', 'plan_credits', e.plan_credits);

      if (e.expiry_date) {
        setRequest(
          body,
          'subscription',
          'expiry_date',
          e.expiry_date.format('YYYYMMDD'),
        );
      }

      setRequest(
        body,
        'aidorganization',
        'organization_name',
        e.organization_name,
      );
      setRequest(body, 'aidorganization', 'company', e.company);
      setRequest(body, 'aidorganization', 'industry', e.industry);
      setRequest(body, 'aidorganization', 'comment', e.comment);
      setRequest(body, 'aidorganization', 'theme', e.theme);

      if (e.user_ff) {
        body['user_ff'] = e.user_ff.join(',');
      }
      if (e.aidorg_ff) {
        setRequest(body, 'aidorganization', 'aidorg_ff', e.aidorg_ff.join(','));
      }
      if (e.sub_ff) {
        setRequest(body, 'subscription', 'sub_ff', e.sub_ff.join(','));
      }

      if (Object.keys(body).length == 0) {
        setErrorMessage('Need at least one change.');
        return;
      }

      requestWithAuth('vault-refcode-overrides', 'POST', null, body).then(
        (response) => {
          if (!response || !response.url) {
            return;
          }

          setSignedRefcode(response.url);
          const expiryText = getRefcodeExpiryFromUrl(response.url);
          setRefcodeExpiryText(expiryText);
        },
      );
    },
    [setRequest],
  );

  return (
    <>
      <Form
        form={form}
        name="signed_refcodes"
        layout="vertical"
        autoComplete="off"
        onFinish={handleFinish}
      >
        <Form.Item
          name="referrer_code"
          label={<div>Referrer Code</div>}
          required
        >
          <Select
            showSearch={true}
            optionFilterProp="label"
            onChange={() => form.setFieldValue('price_point', undefined)}
            options={[
              { value: 'ss_premium', label: 'GTM Premium' },
              { value: 'ss_starter', label: 'GTM Starter' },
              { value: 'ss_recruiter', label: 'Recruiter' },
              { value: 'premium_unlimited', label: 'Premium Unlimited' },
              {
                value: 'premium_unlimited_nwm',
                label: 'Premium Unlimited NWM',
              },
            ]}
          />
        </Form.Item>

        <div>Subscription overrides</div>

        <Form.Item name="owner" label={<div>Account Rep</div>}>
          <Select
            style={{ width: '100%' }}
            filterSort={(a, b) => {
              return a.value > b.value ? 1 : b.value > a.value ? -1 : 0;
            }}
            showSearch={true}
            optionFilterProp="label"
            options={accountRep.map(selectOptionFormatterAccountRep)}
          />
        </Form.Item>
        <Form.Item name="max_seats" label={<div>Max seats</div>}>
          <Input type="number" min={0} />
        </Form.Item>
        <Form.Item name="plan_credits" label={<div>Recurring credits</div>}>
          <Input type="number" min={0} />
        </Form.Item>
        <Form.Item name="sub_ff" label={<div>Subscription Function Flags</div>}>
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            options={functionFlags.map(selectOptionFormatterFunctionFlag)}
          />
        </Form.Item>
        <Form.Item
          name="expiry_date"
          label={<div>Expiration Date (for invoice billing)</div>}
        >
          <DatePicker
            format={{
              format: 'YYYY-MM-DD',
              type: 'mask',
            }}
          />
        </Form.Item>

        <div>Organization overrides</div>

        <Form.Item
          name="organization_name"
          label={<div>Organization Name</div>}
        >
          <Input type="text" />
        </Form.Item>
        <Form.Item name="company" label={<div>Company</div>}>
          <Input type="text" />
        </Form.Item>
        <Form.Item name="industry" label="Industry">
          <Select
            style={{ width: '100%' }}
            showSearch={true}
            optionFilterProp="label"
            options={industryList.map(selectOptionFormatterIndustry)}
          />
        </Form.Item>
        <Form.Item name="comment" label={<div>Comment</div>}>
          <Input type="text" />
        </Form.Item>
        <Form.Item name="theme" label={<div>White Label</div>}>
          <Select
            showSearch={true}
            optionFilterProp="label"
            style={{ width: '100%' }}
            options={theme.map(selectOptionFormatterTheme)}
          />
        </Form.Item>
        <Form.Item
          name="aidorg_ff"
          label={<div>Organization Function Flags</div>}
        >
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            options={functionFlags.map(selectOptionFormatterFunctionFlag)}
          />
        </Form.Item>

        <div>Link overrides</div>

        <Form.Item name="price_point" label={<div>Trial length</div>}>
          <Select
            showSearch={true}
            optionFilterProp="label"
            style={{ width: '100%' }}
            options={Object.entries(pricePoints)
              .filter(
                ([key]) => isUnlimited === UNLIMITED_PRICE_POINTS.includes(key),
              )
              .map(selectOptionFormatterPricePoints)}
          />
        </Form.Item>

        <Form.Item name="campaign" label={<div>Campaign</div>}>
          <Input type="text" />
        </Form.Item>

        <Form.Item name="user_ff" label={<div>User Function Flags</div>}>
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            options={functionFlags.map(selectOptionFormatterFunctionFlag)}
          />
        </Form.Item>

        <Form.Item name="ttl" label={<div>Expiration (hours)</div>}>
          <InputNumber min={1} max={24 * 30} />
        </Form.Item>

        <div>{errorMessage}</div>
        <Form.Item label={null}>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Form.Item>
      </Form>
      {signedRefcode && (
        <div>
          <Input type="text" value={signedRefcode} />
          <div style={{ marginTop: '8px' }}>
            <div>Expires:{refcodeExpiryText}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default SignedRefcodes;
